<template>
  <q-dialog v-model="shown">
    <q-card
      class="column"
    >
      <q-card-section
        class="col-auto row items-center q-gutter-sm"
        style="max-height: 60px;"
      >
        <div class="col text-h6 title">
          {{ $tc('membership.actions.addMember', 2) }}
        </div>
        <div
          class="col-auto row justify-start items-center"
          style="flex-grow: 1"
        >
          <q-btn
            v-close-popup
            flat
            round
            icon="close"
          />
        </div>
      </q-card-section>
      <q-card-section
        class="col items-center justify-center"
      >
        <q-list
          bordered
          separator
          class="q-mx-auto"
          style=" max-width: 700px;"
          :style="{ 'min-width': $q.screen.gt.xs ? '500px' : '100%' }"
        >
          <q-item
            v-for="userClass in availableUserClasses"
            :key="userClass.id"
          >
            <q-item-section>
              <div class="row">
                <div
                  class="col column items-start justify-center"
                >
                  <div class="text-subtitle2">
                    {{ userClass.name }}
                  </div>
                  <div
                    v-if="userClass.description || userClass.ageDescription !== '[]'"
                    class="text-grey-7 text-subtitle"
                  >
                    {{ userClass.description || userClass.ageDescription }}
                  </div>
                </div>
                <div class="col-auto row justify-center items-center">
                  <div
                    v-if="!selectionCount(userClass)"
                    class="col-auto"
                  >
                    <q-btn
                      class="q-px-lg"
                      :label="$t('actions.add')"
                      square
                      outline
                      :disable="hasMaximumMembers"
                      @click.stop="addGuest(userClass)"
                    />
                    <q-tooltip v-if="hasMaximumMembers">
                      {{ $t('membership.memberLimitCountTooltip', { count: currentCircuit.membershipMemberLimit }) }}
                    </q-tooltip>
                  </div>
                  <template v-else>
                    <q-btn
                      class="col-auto"
                      icon="remove"
                      dense
                      square
                      outline
                      :disable="!canRemoveGuest(userClass)"
                      @click.stop="removeGuest(userClass)"
                    />
                    <div
                      v-if="selectionCount(userClass)"
                      class="col-auto text-h5 q-px-md"
                    >
                      {{ selectionCount(userClass) }}
                    </div>
                    <div class="col-auto">
                      <q-btn
                        icon="add"
                        dense
                        square
                        outline
                        :disable="hasMaximumMembers"
                        @click.stop="addGuest(userClass)"
                      />
                      <q-tooltip v-if="hasMaximumMembers">
                        {{ $t('membership.memberLimitCountTooltip', { count: currentCircuit.membershipMemberLimit }) }}
                      </q-tooltip>
                    </div>
                  </template>
                </div>
              </div>
            </q-item-section>
          </q-item>
        </q-list>
      </q-card-section>
      <q-card-actions align="right">
        <q-btn
          :label="$t('actions.cancel')"
          outline
          color="negative"
          @click="cancel"
        />
        <q-btn
          :label="$tc('membership.actions.addMember', 2)"
          color="primary"
          @click="createGuests"
        />
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
import User, { USER_TYPES } from 'src/models/user/User'

export default {
  name: 'AddMembersDialog',
  context: ['currentUser'],
  configuration: ['currentCircuit', 'membershipTypes', 'userClasses'],
  props: {
    member: {
      type: User,
      required: true
    }
  },
  data () {
    return {
      shown: false,
      guestsByUserClass: {}
    }
  },
  computed: {
    availableUserClasses () {
      return this.$consumer ? _.filter(this.userClasses.data, 'availableOnline') : this.userClasses.data
    },
    hasMaximumMembers () {
      const totalMembers = _(Object.values(this.guestsByUserClass)).sum() + this.member.membership.members.data.length
      return this.currentCircuit.enableMembershipMemberLimit &&
            totalMembers >= this.currentCircuit.membershipMemberLimit
    }
  },
  methods: {
    show () {
      this.shown = true
    },
    addGuest (userClass) {
      if (this.hasMaximumMembers) return

      if (this.guestsByUserClass[userClass.id]) {
        this.guestsByUserClass[userClass.id]++
      } else {
        this.guestsByUserClass[userClass.id] = 1
      }
    },
    canRemoveGuest (userClass) {
      return (this.guestsByUserClass[userClass.id] ?? 0) > 0
    },
    removeGuest (userClass) {
      this.guestsByUserClass[userClass.id]--
    },
    selectionCount (userClass) {
      return this.guestsByUserClass[userClass.id] ?? 0
    },
    createGuests () {
      _.each(this.guestsByUserClass, (guestCount, userClassId) => {
        for (let i = 0; i < guestCount; i++) {
          const newUser = new User({
            parent: this.member.membership.members,
            membership: this.member.membership,
            membershipTypeId: this.member.membershipType.id, // Match the membershipType of the owning member
            type: USER_TYPES.GUEST,
            userClassId
          })
          // If the membershipType of the owning member is invalid for the Guest's userclass,
          // use the first applicable membershipType, preferring paid membershipTypes
          if (!this.member.membershipType.membershipUserTypeForUser(newUser)) {
            newUser.membershipType =
            _.find(this.membershipTypes.data, membershipType =>
              !membershipType.free &&
              !membershipType.hide &&
              (membershipType.availableOnline || this.$register) &&
              membershipType.membershipUserTypeForUser(newUser)) ??
            _.find(this.membershipTypes.data, membershipType => membershipType.default)
          }
          this.member.membership.members.add(newUser)
        }
      })

      this.guestsByUserClass = {}
      this.shown = false
    },
    cancel () {
      this.guestsByUserClass = {}
      this.shown = false
    }
  }
}

</script>

<style lang="scss" scoped>
  .title {
    font-family: "Roboto", "-apple-system", "Helvetica Neue", Helvetica, Arial, sans-serif;
  }
  .selected {
    border: solid 4px;
    border-radius: 10px;
    border-color: var(--q-primary);
  }
</style>
